import React, { useContext, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(3),
    borderRadius: 24,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(19,40,60,0.98) 0%, rgba(23,87,138,0.96) 100%)"
        : "linear-gradient(135deg, rgba(241,246,252,1) 0%, rgba(213,232,248,1) 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(36,69,101,0.08)",
    boxShadow: "0 24px 60px rgba(23, 60, 98, 0.14)",
    marginBottom: theme.spacing(3)
  },
  card: {
    borderRadius: 20,
    padding: theme.spacing(2.5),
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(36,69,101,0.08)",
    boxShadow: "0 18px 40px rgba(23, 60, 98, 0.08)"
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 18
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  },
  th: {
    textAlign: "left",
    padding: "14px 12px",
    fontSize: "0.78rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    opacity: 0.72,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  td: {
    padding: "14px 12px",
    verticalAlign: "top",
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  summaryBox: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 14,
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(36,69,101,0.05)"
  }
}));

const TAB_OPTIONS = [
  { key: "tickets", label: "Tickets IA" },
  { key: "clients", label: "Clientes ABC" },
  { key: "pareto", label: "Pareto 80/20" },
  { key: "response-times", label: "Tiempos" },
  { key: "ratings", label: "Calificaciones" }
];

const emptyFilters = {
  dateFrom: "",
  dateTo: "",
  userId: "",
  queueId: "",
  status: "",
  searchParam: "",
  withAI: true
};

const emptySegmentFilters = {
  clientTier: "",
  paretoGroup: ""
};

const STORAGE_KEY = "soluwhats_admin_insights_state_v1";

const buildDefaultFilters = () => ({
  ...emptyFilters,
  dateFrom: new Date(new Date().setDate(1)).toISOString().slice(0, 10),
  dateTo: new Date().toISOString().slice(0, 10)
});

const exportRows = (rows, fileName) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Informe");
  XLSX.writeFile(wb, fileName);
};

const getDaysFromDate = value => {
  if (!value) return null;
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - dateValue.getTime()) / 86400000));
};

const formatDateTime = value => {
  if (!value) return "-";
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return "-";
  return dateValue.toLocaleString("es-CO");
};

const formatDateOnly = value => {
  if (!value) return "-";
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return "-";
  return dateValue.toLocaleDateString("es-CO");
};

const formatMinutesLabel = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return "-";

  if (numericValue < 1) {
    return `${Math.round(numericValue * 60)} seg`;
  }

  if (numericValue < 60) {
    return `${numericValue.toFixed(numericValue < 10 ? 1 : 0)} min`;
  }

  const hours = Math.floor(numericValue / 60);
  const minutes = Math.round(numericValue % 60);
  return `${hours} h ${minutes} min`;
};

const normalizeTierValue = value =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace("CLIENTE ", "");

const contactHasTierTag = (contactTags, tier) =>
  String(contactTags || "")
    .toUpperCase()
    .split(",")
    .map(item => item.trim())
    .includes(`CLIENTE ${tier}`);

const TierChip = ({ value }) => {
  const color =
    value === "A" ? "#1D9A6C" :
      value === "B" ? "#D38A19" : "#6B7280";

  return (
    <Chip
      label={`Cliente ${value}`}
      size="small"
      style={{ backgroundColor: `${color}1A`, color, fontWeight: 700 }}
    />
  );
};

const PriorityChip = ({ value }) => {
  const color =
    value === "Urgente" ? "#D64545" :
      value === "Importante" ? "#D38A19" :
        value === "Seguimiento" ? "#2563EB" : "#64748B";

  return (
    <Chip
      label={value}
      size="small"
      style={{ backgroundColor: `${color}1A`, color, fontWeight: 700 }}
    />
  );
};

const FocusChip = ({ active }) => (
  <Chip
    label={active ? "Top 80/20" : "Resto"}
    size="small"
    style={{
      backgroundColor: active ? "rgba(29,154,108,0.16)" : "rgba(107,114,128,0.16)",
      color: active ? "#1D9A6C" : "#6B7280",
      fontWeight: 700
    }}
  />
);

const getTimeMetricTone = value => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return {
      label: "Sin dato",
      color: "#6B7280",
      background: "rgba(107,114,128,0.16)"
    };
  }

  if (numericValue <= 15) {
    return {
      label: "Optimo",
      color: "#1D9A6C",
      background: "rgba(29,154,108,0.16)"
    };
  }

  if (numericValue <= 60) {
    return {
      label: "Atencion",
      color: "#D38A19",
      background: "rgba(211,138,25,0.16)"
    };
  }

  return {
    label: "Critico",
    color: "#D64545",
    background: "rgba(214,69,69,0.16)"
  };
};

const TimeMetricChip = ({ value, prefix = "" }) => {
  const tone = getTimeMetricTone(value);
  const timeLabel = formatMinutesLabel(value);
  const label = prefix ? `${prefix}: ${timeLabel}` : timeLabel;

  return (
    <Chip
      label={`${label} · ${tone.label}`}
      size="small"
      style={{
        backgroundColor: tone.background,
        color: tone.color,
        fontWeight: 700
      }}
    />
  );
};

const ReadRateChip = ({ value }) => {
  const numericValue = Number(value || 0);
  const color =
    numericValue >= 80 ? "#1D9A6C" :
      numericValue >= 50 ? "#D38A19" : "#D64545";

  return (
    <Chip
      label={`${numericValue}% leidos`}
      size="small"
      style={{
        backgroundColor: `${color}1A`,
        color,
        fontWeight: 700
      }}
    />
  );
};

const formatContextDateTime = value => {
  if (!value) return "";
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return "";
  return dateValue.toLocaleString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
};

const getMessagePreviewText = message => {
  const body = String(message?.body || message?.transcription || "").trim();
  if (body) return body;

  const mediaType = String(message?.mediaType || "").toLowerCase();
  if (mediaType.includes("audio")) return "[Audio]";
  if (mediaType.includes("image")) return "[Imagen]";
  if (mediaType.includes("video")) return "[Video]";
  if (mediaType.includes("application") || mediaType.includes("document")) return "[Archivo]";
  return "[Mensaje sin texto]";
};

const buildTicketSummary = rows => ({
  total: rows.length,
  urgent: rows.filter(item => item.priority === "Urgente").length,
  important: rows.filter(item => item.priority === "Importante").length,
  followUp: rows.filter(item => item.priority === "Seguimiento").length,
  aiCount: rows.filter(item => item.analyzedBy === "ia").length
});

const buildClientSummary = rows => ({
  total: rows.length,
  tierA: rows.filter(item => item.tier === "A").length,
  tierB: rows.filter(item => item.tier === "B").length,
  stale: rows.filter(item => {
    const days = getDaysFromDate(item.lastTicketAt);
    return days === null || days > 30;
  }).length,
  activeA: rows.filter(item => item.tier === "A" && Number(item.openTickets || 0) > 0).length
});

const buildParetoSummary = rows => {
  const focusRows = rows.filter(item => item.isTop8020);
  const topContribution = focusRows.reduce((sum, item) => sum + Number(item.contributionPct || 0), 0);

  return {
    total: rows.length,
    focusRows: focusRows.length,
    topContribution: Number(topContribution.toFixed(2)),
    openInsideFocus: focusRows.filter(item => Number(item.openTickets || 0) > 0).length
  };
};

const buildRatingSummary = rows => ({
  total: rows.length,
  avgStars:
    rows.length > 0
      ? Number((rows.reduce((sum, item) => sum + Number(item.stars || 0), 0) / rows.length).toFixed(2))
      : 0,
  critical: rows.filter(item => Number(item.stars || 0) <= 2).length,
  positive: rows.filter(item => Number(item.stars || 0) >= 4).length
});

const buildResponseSummary = meta => ({
  total: Number(meta?.totalTickets || 0),
  avgFirstResponseMinutes: Number(meta?.avgFirstResponseMinutes || 0),
  firstResponseWithin15mPct: Number(meta?.firstResponseWithin15mPct || 0),
  avgAgentResponseMinutes: Number(meta?.avgAgentResponseMinutes || 0),
  avgClientResponseMinutes: Number(meta?.avgClientResponseMinutes || 0),
  avgClientReadMinutes: Number(meta?.avgClientReadMinutes || 0),
  clientReadRatePct: Number(meta?.clientReadRatePct || 0),
  ticketsWithFirstResponse: Number(meta?.ticketsWithFirstResponse || 0)
});

export default function AdminInsights() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  const [filters, setFilters] = useState(buildDefaultFilters);
  const [loading, setLoading] = useState(false);
  const [applyingTags, setApplyingTags] = useState(false);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [ticketData, setTicketData] = useState({ meta: { total: 0, aiUsed: false }, rows: [] });
  const [clientData, setClientData] = useState({ meta: { total: 0, tiers: { A: 0, B: 0, C: 0 } }, rows: [] });
  const [paretoData, setParetoData] = useState({ meta: { totalClients: 0, totalScore: 0, topClients: 0, topContributionPct: 0, suggestedFocusPct: 0 }, rows: [] });
  const [responseTimesData, setResponseTimesData] = useState({ meta: {}, rows: [] });
  const [ratingData, setRatingData] = useState({ meta: { total: 0, avgStars: 0 }, rows: [] });
  const [responseSortBy, setResponseSortBy] = useState("avgAgentResponseMinutes");
  const [responseSortDirection, setResponseSortDirection] = useState("desc");
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextRow, setContextRow] = useState(null);
  const [contextMessages, setContextMessages] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState("");
  const [loadedTabs, setLoadedTabs] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [segmentFilters, setSegmentFilters] = useState(emptySegmentFilters);

  const activeTab = useMemo(() => {
    const current = new URLSearchParams(location.search).get("tab");
    return TAB_OPTIONS.some(item => item.key === current) ? current : "tickets";
  }, [location.search]);

  const ticketSummary = useMemo(() => buildTicketSummary(ticketData.rows || []), [ticketData.rows]);
  const filteredClientRows = useMemo(() => {
    const rows = clientData.rows || [];
    if (!segmentFilters.clientTier) return rows;
    return rows.filter(item => {
      const rowTier = normalizeTierValue(item.tier);
      const selectedTier = normalizeTierValue(segmentFilters.clientTier);
      return rowTier === selectedTier || contactHasTierTag(item.contactTags, selectedTier);
    });
  }, [clientData.rows, segmentFilters.clientTier]);

  const filteredParetoRows = useMemo(() => {
    const rows = paretoData.rows || [];
    if (!segmentFilters.paretoGroup) return rows;
    if (segmentFilters.paretoGroup === "top") {
      return rows.filter(item => item.isTop8020);
    }
    if (segmentFilters.paretoGroup === "rest") {
      return rows.filter(item => !item.isTop8020);
    }
    return rows;
  }, [paretoData.rows, segmentFilters.paretoGroup]);

  const clientSummary = useMemo(() => buildClientSummary(filteredClientRows), [filteredClientRows]);
  const paretoSummary = useMemo(() => buildParetoSummary(filteredParetoRows), [filteredParetoRows]);
  const responseSummary = useMemo(() => buildResponseSummary(responseTimesData.meta), [responseTimesData.meta]);
  const ratingSummary = useMemo(() => buildRatingSummary(ratingData.rows || []), [ratingData.rows]);
  const sortedResponseRows = useMemo(() => {
    const rows = [...(responseTimesData.rows || [])];
    const directionFactor = responseSortDirection === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      const aValue = Number(a?.[responseSortBy] ?? -1);
      const bValue = Number(b?.[responseSortBy] ?? -1);

      const normalizedA = Number.isFinite(aValue) ? aValue : -1;
      const normalizedB = Number.isFinite(bValue) ? bValue : -1;

      if (normalizedA === normalizedB) {
        return Number(b.ticketId || 0) - Number(a.ticketId || 0);
      }

      return (normalizedA - normalizedB) * directionFactor;
    });

    return rows;
  }, [responseTimesData.rows, responseSortBy, responseSortDirection]);
  const contextExcerpt = useMemo(() => {
    if (!contextRow || !contextMessages.length) return [];

    const firstInboundTime = contextRow.firstInboundAt ? new Date(contextRow.firstInboundAt).getTime() : null;
    const firstAgentReplyTime = contextRow.firstAgentReplyAt ? new Date(contextRow.firstAgentReplyAt).getTime() : null;

    if (!firstInboundTime && !firstAgentReplyTime) {
      return contextMessages.slice(-8);
    }

    let focusIndex = contextMessages.findIndex(message => {
      const createdTime = new Date(message.createdAt).getTime();
      if (!Number.isFinite(createdTime)) return false;
      if (firstAgentReplyTime) {
        return createdTime >= firstAgentReplyTime;
      }
      return createdTime >= firstInboundTime;
    });

    if (focusIndex < 0) {
      focusIndex = Math.max(0, contextMessages.length - 1);
    }

    const start = Math.max(0, focusIndex - 4);
    const end = Math.min(contextMessages.length, focusIndex + 4);
    return contextMessages.slice(start, end);
  }, [contextMessages, contextRow]);

  useEffect(() => {
    if (!(user?.super || user?.profile === "admin")) {
      history.replace("/tickets");
    }
  }, [history, user]);

  useEffect(() => {
    try {
      const rawState = localStorage.getItem(STORAGE_KEY);
      if (!rawState) return;

      const parsed = JSON.parse(rawState);
      if (parsed?.filters) {
        setFilters(prev => ({ ...prev, ...parsed.filters }));
      }
      if (parsed?.segmentFilters) {
        setSegmentFilters(prev => ({ ...prev, ...parsed.segmentFilters }));
      }
      if (parsed?.datasets?.tickets) setTicketData(parsed.datasets.tickets);
      if (parsed?.datasets?.clients) setClientData(parsed.datasets.clients);
      if (parsed?.datasets?.pareto) setParetoData(parsed.datasets.pareto);
      if (parsed?.datasets?.responseTimes) setResponseTimesData(parsed.datasets.responseTimes);
      if (parsed?.datasets?.ratings) setRatingData(parsed.datasets.ratings);
      if (parsed?.lastSavedAt) setLastSavedAt(parsed.lastSavedAt);
      if (Array.isArray(parsed?.loadedTabs)) setLoadedTabs(parsed.loadedTabs);
    } catch (_error) {
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setHydrated(true);
    }
  }, []);

  useEffect(() => {
    const loadSupportData = async () => {
      try {
        const [{ data: usersResponse }, { data: queuesResponse }] = await Promise.all([
          api.get("/users"),
          api.get("/queue")
        ]);
        setUsers(Array.isArray(usersResponse?.users) ? usersResponse.users : []);
        setQueues(Array.isArray(queuesResponse) ? queuesResponse : Array.isArray(queuesResponse?.queues) ? queuesResponse.queues : []);
      } catch (err) {
        toastError(err);
      }
    };

    loadSupportData();
  }, []);

  const buildCommonParams = () => ({
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    userId: filters.userId,
    queueId: filters.queueId,
    status: filters.status,
    searchParam: filters.searchParam
  });

  const persistAdminInsightsState = payload => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      setLastSavedAt(payload.lastSavedAt);
      if (Array.isArray(payload.loadedTabs)) {
        setLoadedTabs(payload.loadedTabs);
      }
    } catch (_error) {
      // Almacenamiento local opcional.
    }
  };

  const loadData = async ({ useAI = false } = {}) => {
    setLoading(true);
    try {
      const commonParams = buildCommonParams();
      const nextSavedAt = new Date().toISOString();
      let nextTicketData = ticketData;
      let nextClientData = clientData;
      let nextParetoData = paretoData;
      let nextResponseTimesData = responseTimesData;
      let nextRatingData = ratingData;

      if (activeTab === "tickets") {
        const { data } = await api.get("/admin-insights/tickets", {
          params: {
            ...commonParams,
            page: 1,
            pageSize: 20,
            withAI: useAI ? "true" : "false"
          }
        });
        nextTicketData = data;
        setTicketData(data);
      } else if (activeTab === "clients") {
        const { data } = await api.get("/admin-insights/clients-abc", {
          params: {
            ...commonParams,
            withAI: useAI ? "true" : "false"
          }
        });
        nextClientData = data;
        setClientData(data);
      } else if (activeTab === "pareto") {
        const { data } = await api.get("/admin-insights/clients-pareto", {
          params: {
            ...commonParams,
            withAI: useAI ? "true" : "false"
          }
        });
        nextParetoData = data;
        setParetoData(data);
      } else if (activeTab === "response-times") {
        const { data } = await api.get("/admin-insights/response-times", {
          params: commonParams
        });
        nextResponseTimesData = data;
        setResponseTimesData(data);
      } else {
        const { data } = await api.get("/admin-insights/ratings", {
          params: commonParams
        });
        nextRatingData = data;
        setRatingData(data);
      }

      const nextLoadedTabs = Array.from(new Set([...(loadedTabs || []), activeTab]));
      persistAdminInsightsState({
        filters,
        segmentFilters,
        datasets: {
          tickets: nextTicketData,
          clients: nextClientData,
          pareto: nextParetoData,
          responseTimes: nextResponseTimesData,
          ratings: nextRatingData
        },
        loadedTabs: nextLoadedTabs,
        lastSavedAt: nextSavedAt
      });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hydrated) return;
    if (loadedTabs.includes(activeTab)) return;
    loadData({ useAI: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hydrated]);

  const handleTabChange = (_event, value) => {
    history.push(`/admin-insights?tab=${value}`);
  };

  const handleResetFilters = () => {
    const nextFilters = buildDefaultFilters();
    setFilters(nextFilters);
    persistAdminInsightsState({
      filters: nextFilters,
      segmentFilters: emptySegmentFilters,
      datasets: {
        tickets: ticketData,
        clients: clientData,
        pareto: paretoData,
        responseTimes: responseTimesData,
        ratings: ratingData
      },
      loadedTabs,
      lastSavedAt: new Date().toISOString()
    });
  };

  const handleRequeryIA = async () => {
    await loadData({ useAI: true });
    toast.success("Consulta actualizada con IA.");
  };

  const handleExport = () => {
    if (activeTab === "tickets") {
      exportRows(
        ticketData.rows.map(item => ({
          Ticket: item.id,
          Cliente: item.contactName,
          Tema: item.theme,
          Clasificacion: item.classification,
          Prioridad: item.priority,
          Usuario: item.userName,
          Cola: item.queueName,
          Estado: item.status,
          Resumen: item.summary,
          FechaCreacion: formatDateTime(item.createdAt),
          FechaCierre: formatDateTime(item.closedAt),
          Calificacion: item.stars || 0,
          AnalizadoPor: item.analyzedBy
        })),
        "informe-tickets-inteligente.xlsx"
      );
      return;
    }

    if (activeTab === "clients") {
      exportRows(
        filteredClientRows.map(item => ({
          Cliente: item.contactName,
          Tipo: item.tier,
          Puntaje: item.score,
          PerfilNegocio: item.businessProfile,
          ResumenContenido: item.contentSummary,
          Justificacion: item.rationale,
          Tickets: item.totalTickets,
          Abiertos: item.openTickets,
          Cerrados: item.closedTickets,
          RatingPromedio: item.avgStars,
          Colas: item.queues,
          Etiquetas: item.contactTags,
          UltimoTicket: formatDateOnly(item.lastTicketAt),
          DiasSinMovimiento: getDaysFromDate(item.lastTicketAt),
          Recomendacion: item.recommendation,
          AnalizadoPor: item.analyzedBy
        })),
        "clientes-abc.xlsx"
      );
      return;
    }

    if (activeTab === "pareto") {
      exportRows(
        filteredParetoRows.map(item => ({
          Cliente: item.contactName,
          Tipo: item.tier,
          Puntaje: item.score,
          PerfilNegocio: item.businessProfile,
          ResumenContenido: item.contentSummary,
          Justificacion: item.rationale,
          AportePct: item.contributionPct,
          AcumuladoPct: item.cumulativePct,
          Top8020: item.isTop8020 ? "Si" : "No",
          Tickets: item.totalTickets,
          Abiertos: item.openTickets,
          Cerrados: item.closedTickets,
          RatingPromedio: item.avgStars,
          Colas: item.queues,
          UltimoTicket: formatDateOnly(item.lastTicketAt),
          DiasSinMovimiento: getDaysFromDate(item.lastTicketAt),
          Recomendacion: item.recommendation,
          AnalizadoPor: item.analyzedBy
        })),
        "clientes-pareto-80-20.xlsx"
      );
      return;
    }

    if (activeTab === "response-times") {
      exportRows(
        sortedResponseRows.map(item => ({
          Ticket: item.ticketId,
          Cliente: item.contactName,
          Usuario: item.userName,
          Cola: item.queueName,
          Estado: item.status,
          FechaCreacion: formatDateTime(item.createdAt),
          FechaCierre: formatDateTime(item.closedAt),
          PrimeraEntradaCliente: formatDateTime(item.firstInboundAt),
          PrimeraRespuestaAsesor: formatDateTime(item.firstAgentReplyAt),
          PrimeraRespuestaHabil: item.firstResponseMinutes,
          Cumple15Min: item.firstResponseWithin15m ? "Si" : "No",
          PromedioRespuestaAsesor: item.avgAgentResponseMinutes,
          PromedioRespuestaCliente: item.avgClientResponseMinutes,
          PromedioLecturaCliente: item.avgClientReadMinutes,
          TasaLecturaPct: item.clientReadRatePct,
          MensajesCliente: item.customerMessages,
          MensajesAsesor: item.manualAgentMessages,
          TurnosAsesorMedidos: item.measuredAgentTurns,
          TurnosClienteMedidos: item.measuredClientTurns,
          EventosLeidos: item.readEvents
        })),
        "kpi-tiempos-respuesta.xlsx"
      );
      return;
    }

    exportRows(
      ratingData.rows.map(item => ({
        Fecha: formatDateTime(item.createdAt),
        Cliente: item.clientName,
        Usuario: item.agentName,
        Cola: item.queueName,
        Estrellas: item.stars,
        Sentimiento: item.sentiment,
        Comentario: item.comment
      })),
      "calificaciones-gerenciales.xlsx"
    );
  };

  const handleApplyClientAbcTags = async () => {
    const confirmed = window.confirm(
      "Se aplicara la clasificacion ABC solo a los contactos visibles en este filtro. Solo se actualizaran las etiquetas CLIENTE A, CLIENTE B y CLIENTE C. Desea continuar?"
    );

    if (!confirmed) return;

    setApplyingTags(true);
    try {
      const { data } = await api.post("/admin-insights/clients-abc/apply-tags", buildCommonParams());
      toast.success(data?.message || "Clasificacion ABC aplicada.");
      await loadData();
    } catch (err) {
      toastError(err);
    } finally {
      setApplyingTags(false);
    }
  };

  const handleCloseContextModal = () => {
    setContextModalOpen(false);
    setContextLoading(false);
    setContextRow(null);
    setContextMessages([]);
  };

  const handleOpenResponseContext = async item => {
    setContextRow(item);
    setContextModalOpen(true);
    setContextLoading(true);
    setContextMessages([]);

    try {
      let pageNumber = 1;
      let hasMore = true;
      let collected = [];

      while (hasMore && pageNumber <= 8) {
        const { data } = await api.get(`/messages/${item.ticketId}`, {
          params: { pageNumber }
        });

        const pageMessages = Array.isArray(data?.messages) ? data.messages : [];
        collected = [...collected, ...pageMessages];
        hasMore = Boolean(data?.hasMore);
        pageNumber += 1;
      }

      const uniqueMessages = collected.filter((message, index, array) =>
        array.findIndex(current => current.id === message.id) === index
      );

      uniqueMessages.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      setContextMessages(uniqueMessages);
    } catch (err) {
      toastError(err);
      setContextModalOpen(false);
    } finally {
      setContextLoading(false);
    }
  };

  const renderCards = () => {
    if (activeTab === "tickets") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Tickets analizados</Typography>
              <Typography variant="h4">{ticketSummary.total}</Typography>
              <Typography variant="body2" color="textSecondary">
                Resultado del rango filtrado.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Urgentes</Typography>
              <Typography variant="h4">{ticketSummary.urgent}</Typography>
              <Typography variant="body2" color="textSecondary">
                Casos con riesgo operativo o bloqueo.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Seguimientos</Typography>
              <Typography variant="h4">{ticketSummary.followUp}</Typography>
              <Typography variant="body2" color="textSecondary">
                Conversaciones que conviene retomar.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Analizados por IA</Typography>
              <Typography variant="h4">{ticketSummary.aiCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Si falta clave IA, el modulo usa reglas seguras.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    if (activeTab === "clients") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Clientes evaluados</Typography>
              <Typography variant="h4">{clientSummary.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Tipo A</Typography>
              <Typography variant="h4">{clientSummary.tierA}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Tipo A con abiertos</Typography>
              <Typography variant="h4">{clientSummary.activeA}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Analisis de contenido</Typography>
              <Typography variant="h4">{clientData.meta?.aiUsed ? "IA" : "Reglas"}</Typography>
              <Typography variant="body2" color="textSecondary">
                La clasificacion prioriza valor del contenido, no volumen.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    if (activeTab === "pareto") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Clientes analizados</Typography>
              <Typography variant="h4">{paretoSummary.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Grupo foco</Typography>
              <Typography variant="h4">{paretoSummary.focusRows}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Aporte acumulado</Typography>
              <Typography variant="h4">{paretoSummary.topContribution}%</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper className={classes.card}>
              <Typography variant="overline">Foco con abiertos</Typography>
              <Typography variant="h4">{paretoSummary.openInsideFocus}</Typography>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    if (activeTab === "response-times") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <Paper className={classes.card}>
              <Typography variant="overline">Tickets medidos</Typography>
              <Typography variant="h4">{responseSummary.total}</Typography>
              <Typography variant="body2" color="textSecondary">
                Solo cuenta tiempos dentro del horario laboral configurado.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper className={classes.card}>
              <Typography variant="overline">Primera respuesta</Typography>
              <Typography variant="h4">{formatMinutesLabel(responseSummary.avgFirstResponseMinutes)}</Typography>
              <Typography variant="body2" color="textSecondary">
                Promedio habil del equipo en la primera respuesta.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper className={classes.card}>
              <Typography variant="overline">Resp. del asesor</Typography>
              <Typography variant="h4">{formatMinutesLabel(responseSummary.avgAgentResponseMinutes)}</Typography>
              <Typography variant="body2" color="textSecondary">
                Cuanto tardamos nosotros en responder, en promedio habil.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper className={classes.card}>
              <Typography variant="overline">Resp. del cliente</Typography>
              <Typography variant="h4">{formatMinutesLabel(responseSummary.avgClientResponseMinutes)}</Typography>
              <Typography variant="body2" color="textSecondary">
                Cuanto tarda el cliente en contestar, en promedio habil.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper className={classes.card}>
              <Typography variant="overline">SLA 15 min</Typography>
              <Typography variant="h4">{responseSummary.firstResponseWithin15mPct}%</Typography>
              <Typography variant="body2" color="textSecondary">
                Porcentaje de tickets con primera respuesta dentro de 15 minutos habiles.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2}>
            <Paper className={classes.card}>
              <Typography variant="overline">Lectura del cliente</Typography>
              <Typography variant="h4">{responseSummary.clientReadRatePct}%</Typography>
              <Typography variant="body2" color="textSecondary">
                Mensajes del asesor que el cliente alcanzo a leer.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.card}>
            <Typography variant="overline">Calificaciones</Typography>
            <Typography variant="h4">{ratingSummary.total}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.card}>
            <Typography variant="overline">Promedio estrellas</Typography>
            <Typography variant="h4">{ratingSummary.avgStars}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper className={classes.card}>
            <Typography variant="overline">Alertas por baja calificacion</Typography>
            <Typography variant="h4">{ratingSummary.critical}</Typography>
            <Typography variant="body2" color="textSecondary">
              Este reporte muestra solo el nombre del cliente.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <MainContainer>
      <Paper className={classes.hero}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="overline" style={{ fontWeight: 700, letterSpacing: "0.14em" }}>
              Modulo gerencial
            </Typography>
            <Title>Informes inteligentes</Title>
            <Typography variant="body1" style={{ maxWidth: 760, opacity: 0.86 }}>
              Este modulo queda aislado del flujo operativo. Sirve para revisar entre fechas que paso en los tickets,
              como se estan moviendo los clientes y como estan calificando el servicio.
            </Typography>
            <Typography variant="body2" style={{ marginTop: 12, opacity: 0.74 }}>
              La consulta queda guardada localmente en este navegador para no perder filtros ni resultados al recargar.
            </Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper className={classes.summaryBox} elevation={0}>
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                Regla activa
              </Typography>
              <Typography variant="body2" color="textSecondary">
                No se toca ni se rompe nada del flujo operativo actual. Todo este bloque funciona como capa adicional de analisis.
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
                Ultimo guardado local: {lastSavedAt ? formatDateTime(lastSavedAt) : "Aun no hay consulta guardada"}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              {TAB_OPTIONS.map(item => (
                <Tab key={item.key} value={item.key} label={item.label} />
              ))}
            </Tabs>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1} justifyContent="flex-end">
              <Grid item xs={6} sm={4}>
                <Button fullWidth variant="outlined" color="primary" onClick={handleExport}>
                  Exportar
                </Button>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Button fullWidth variant="outlined" onClick={handleResetFilters}>
                  Limpiar
                </Button>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Button fullWidth variant="contained" color="primary" onClick={() => loadData({ useAI: false })} disabled={loading || applyingTags}>
                  Ejecutar
                </Button>
              </Grid>
              {["tickets", "clients", "pareto"].includes(activeTab) && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={handleRequeryIA}
                    disabled={loading || applyingTags}
                  >
                    Reconsultar con IA
                  </Button>
                </Grid>
              )}
              {activeTab === "clients" && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    onClick={handleApplyClientAbcTags}
                    disabled={loading || applyingTags}
                  >
                    {applyingTags ? "Aplicando..." : "Aplicar clasificacion ABC a los visibles"}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.card} style={{ marginBottom: 24 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Fecha inicial"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.dateFrom}
              onChange={e => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Fecha final"
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filters.dateTo}
              onChange={e => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Usuario</InputLabel>
              <Select
                label="Usuario"
                value={filters.userId}
                onChange={e => setFilters(prev => ({ ...prev, userId: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {users.map(item => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Cola</InputLabel>
              <Select
                label="Cola"
                value={filters.queueId}
                onChange={e => setFilters(prev => ({ ...prev, queueId: e.target.value }))}
              >
                <MenuItem value="">Todas</MenuItem>
                {queues.map(item => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={filters.status}
                onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="open">Abiertos</MenuItem>
                <MenuItem value="pending">Pendientes</MenuItem>
                <MenuItem value="closed">Cerrados</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Buscar"
              variant="outlined"
              size="small"
              value={filters.searchParam}
              onChange={e => setFilters(prev => ({ ...prev, searchParam: e.target.value }))}
            />
          </Grid>
          {activeTab === "clients" && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Tipo ABC</InputLabel>
                <Select
                  label="Tipo ABC"
                  value={segmentFilters.clientTier}
                  renderValue={selected => {
                    if (!selected) return "Todos";
                    return `Solo ${selected}`;
                  }}
                  onChange={e => setSegmentFilters(prev => ({ ...prev, clientTier: e.target.value }))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="A">Solo A</MenuItem>
                  <MenuItem value="B">Solo B</MenuItem>
                  <MenuItem value="C">Solo C</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {activeTab === "pareto" && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Grupo 80/20</InputLabel>
                <Select
                  label="Grupo 80/20"
                  value={segmentFilters.paretoGroup}
                  renderValue={selected => {
                    if (!selected) return "Todos";
                    return selected === "top" ? "20% foco" : "80% restante";
                  }}
                  onChange={e => setSegmentFilters(prev => ({ ...prev, paretoGroup: e.target.value }))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="top">20% foco</MenuItem>
                  <MenuItem value="rest">80% restante</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {activeTab === "response-times" && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Ordenar tiempos por</InputLabel>
                <Select
                  label="Ordenar tiempos por"
                  value={responseSortBy}
                  onChange={e => setResponseSortBy(e.target.value)}
                >
                  <MenuItem value="firstResponseMinutes">Primera respuesta</MenuItem>
                  <MenuItem value="avgAgentResponseMinutes">Respuesta del asesor</MenuItem>
                  <MenuItem value="avgClientResponseMinutes">Respuesta del cliente</MenuItem>
                  <MenuItem value="avgClientReadMinutes">Tiempo de lectura del cliente</MenuItem>
                  <MenuItem value="clientReadRatePct">% de lectura</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {activeTab === "response-times" && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Sentido</InputLabel>
                <Select
                  label="Sentido"
                  value={responseSortDirection}
                  onChange={e => setResponseSortDirection(e.target.value)}
                >
                  <MenuItem value="desc">Mayor a menor</MenuItem>
                  <MenuItem value="asc">Menor a mayor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {renderCards()}

      <Paper className={classes.card} style={{ marginTop: 24 }}>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={220}>
            <CircularProgress />
          </Box>
        ) : activeTab === "tickets" ? (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Cliente</th>
                  <th className={classes.th}>Tema</th>
                  <th className={classes.th}>Clasificacion</th>
                  <th className={classes.th}>Prioridad</th>
                  <th className={classes.th}>Responsable</th>
                  <th className={classes.th}>Estado</th>
                  <th className={classes.th}>Resumen</th>
                </tr>
              </thead>
              <tbody>
                {ticketData.rows.map(item => (
                  <tr key={item.id}>
                    <td className={classes.td}>
                      <strong>{item.contactName}</strong>
                      <div style={{ opacity: 0.7 }}>Ticket #{item.id}</div>
                      <div style={{ opacity: 0.7 }}>
                        {item.stars ? `${item.stars} estrellas` : "Sin calificacion"}
                      </div>
                    </td>
                    <td className={classes.td}>{item.theme}</td>
                    <td className={classes.td}>
                      <strong>{item.classification}</strong>
                      <div style={{ opacity: 0.72, marginTop: 4 }}>{item.rationale}</div>
                    </td>
                    <td className={classes.td}>
                      <PriorityChip value={item.priority} />
                      <div style={{ opacity: 0.7, marginTop: 8 }}>
                        {item.status === "open" ? "Atencion inmediata" :
                          item.status === "pending" ? "Requiere seguimiento" :
                            "Ya cerrado"}
                      </div>
                    </td>
                    <td className={classes.td}>
                      <strong>{item.userName}</strong>
                      <div style={{ opacity: 0.7 }}>{item.queueName}</div>
                    </td>
                    <td className={classes.td}>
                      <strong>{item.status}</strong>
                      <div style={{ opacity: 0.7 }}>{formatDateTime(item.createdAt)}</div>
                      {item.closedAt && (
                        <div style={{ opacity: 0.7 }}>Cerrado: {formatDateTime(item.closedAt)}</div>
                      )}
                    </td>
                    <td className={classes.td}>
                      <div>{item.summary}</div>
                      <div style={{ opacity: 0.65, marginTop: 6 }}>
                        Analizado por: {item.analyzedBy === "ia" ? "IA" : "reglas"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === "clients" ? (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Cliente</th>
                  <th className={classes.th}>Tipo</th>
                  <th className={classes.th}>Perfil</th>
                  <th className={classes.th}>Puntaje</th>
                  <th className={classes.th}>Movimiento</th>
                  <th className={classes.th}>Contenido clave</th>
                  <th className={classes.th}>Etiquetas actuales</th>
                  <th className={classes.th}>Recomendacion</th>
                </tr>
              </thead>
              <tbody>
                {filteredClientRows.map(item => (
                  <tr key={item.contactId}>
                    <td className={classes.td}>
                      <strong>{item.contactName}</strong>
                      <div style={{ opacity: 0.7 }}>{item.queues || "Sin cola principal"}</div>
                      <div style={{ opacity: 0.7 }}>Ultimo ticket: {formatDateOnly(item.lastTicketAt)}</div>
                    </td>
                    <td className={classes.td}><TierChip value={item.tier} /></td>
                    <td className={classes.td}>
                      <strong>{item.businessProfile || "Sin perfil"}</strong>
                      <div style={{ opacity: 0.7, marginTop: 4 }}>
                        Analizado por: {item.analyzedBy === "ia" ? "IA" : "reglas"}
                      </div>
                    </td>
                    <td className={classes.td}>
                      <strong>{item.score}</strong>
                      <div style={{ opacity: 0.72, marginTop: 4 }}>{item.rationale}</div>
                    </td>
                    <td className={classes.td}>
                      <div>Total: {item.totalTickets}</div>
                      <div>Abiertos: {item.openTickets}</div>
                      <div>Cerrados: {item.closedTickets}</div>
                      <div>Rating: {item.avgStars || "-"}</div>
                      <div>Inactividad: {getDaysFromDate(item.lastTicketAt) ?? "-"} dias</div>
                    </td>
                    <td className={classes.td}>
                      {item.contentSummary || "Sin contenido suficiente"}
                    </td>
                    <td className={classes.td}>{item.contactTags || "Sin etiquetas"}</td>
                    <td className={classes.td}>{item.recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === "pareto" ? (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Cliente</th>
                  <th className={classes.th}>Grupo foco</th>
                  <th className={classes.th}>Perfil</th>
                  <th className={classes.th}>Aporte</th>
                  <th className={classes.th}>Acumulado</th>
                  <th className={classes.th}>Movimiento</th>
                  <th className={classes.th}>Contenido clave</th>
                  <th className={classes.th}>Lectura gerencial</th>
                </tr>
              </thead>
              <tbody>
                {filteredParetoRows.map(item => (
                  <tr key={`pareto-${item.contactId}`}>
                    <td className={classes.td}>
                      <strong>{item.contactName}</strong>
                      <div style={{ opacity: 0.7 }}>{item.queues || "Sin cola principal"}</div>
                      <div style={{ opacity: 0.7 }}>Ultimo movimiento: {formatDateOnly(item.lastTicketAt)}</div>
                    </td>
                    <td className={classes.td}>
                      <FocusChip active={item.isTop8020} />
                      <div style={{ marginTop: 6 }}><TierChip value={item.tier} /></div>
                    </td>
                    <td className={classes.td}>
                      <strong>{item.businessProfile || "Sin perfil"}</strong>
                      <div style={{ opacity: 0.7, marginTop: 4 }}>
                        Analizado por: {item.analyzedBy === "ia" ? "IA" : "reglas"}
                      </div>
                    </td>
                    <td className={classes.td}>
                      <strong>{item.contributionPct}%</strong>
                      <div style={{ opacity: 0.72, marginTop: 4 }}>Puntaje: {item.score}</div>
                    </td>
                    <td className={classes.td}>
                      <strong>{item.cumulativePct}%</strong>
                    </td>
                    <td className={classes.td}>
                      <div>Total: {item.totalTickets}</div>
                      <div>Abiertos: {item.openTickets}</div>
                      <div>Cerrados: {item.closedTickets}</div>
                      <div>Rating: {item.avgStars || "-"}</div>
                      <div>Inactividad: {getDaysFromDate(item.lastTicketAt) ?? "-"} dias</div>
                    </td>
                    <td className={classes.td}>
                      {item.contentSummary || "Sin contenido suficiente"}
                    </td>
                    <td className={classes.td}>
                      <div>{item.recommendation}</div>
                      <div style={{ opacity: 0.72, marginTop: 4 }}>{item.rationale}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === "response-times" ? (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Ticket</th>
                  <th className={classes.th}>Primera respuesta</th>
                  <th className={classes.th}>Promedios habiles</th>
                  <th className={classes.th}>Lectura del cliente</th>
                  <th className={classes.th}>Volumen</th>
                  <th className={classes.th}>Fechas clave</th>
                </tr>
              </thead>
              <tbody>
                {sortedResponseRows.map(item => (
                  <tr key={`response-${item.ticketId}`}>
                    <td className={classes.td}>
                      <strong>{item.contactName}</strong>
                      <div style={{ opacity: 0.7 }}>Ticket #{item.ticketId}</div>
                      <div style={{ opacity: 0.7 }}>{item.userName} · {item.queueName}</div>
                      <div style={{ opacity: 0.7 }}>Estado: {item.status}</div>
                      <div style={{ marginTop: 10 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleOpenResponseContext(item)}
                        >
                          Ver contexto
                        </Button>
                      </div>
                    </td>
                    <td className={classes.td}>
                      <TimeMetricChip value={item.firstResponseMinutes} />
                      <div style={{ opacity: 0.72, marginTop: 4 }}>
                        {item.firstResponseWithin15m ? "Cumple SLA de 15 min" : "Fuera del SLA de 15 min"}
                      </div>
                    </td>
                    <td className={classes.td}>
                      <div style={{ marginBottom: 6 }}>
                        <TimeMetricChip value={item.avgAgentResponseMinutes} prefix="Asesor" />
                      </div>
                      <div>
                        <TimeMetricChip value={item.avgClientResponseMinutes} prefix="Cliente" />
                      </div>
                    </td>
                    <td className={classes.td}>
                      <div style={{ marginBottom: 6 }}>
                        <ReadRateChip value={item.clientReadRatePct} />
                      </div>
                      <div>Promedio lectura: {formatMinutesLabel(item.avgClientReadMinutes)}</div>
                      <div>Eventos leidos: {item.readEvents}</div>
                    </td>
                    <td className={classes.td}>
                      <div>Mensajes cliente: {item.customerMessages}</div>
                      <div>Mensajes asesor: {item.manualAgentMessages}</div>
                      <div>Turnos asesor: {item.measuredAgentTurns}</div>
                      <div>Turnos cliente: {item.measuredClientTurns}</div>
                    </td>
                    <td className={classes.td}>
                      <div>Creado: {formatDateTime(item.createdAt)}</div>
                      <div>Primer cliente: {formatDateTime(item.firstInboundAt)}</div>
                      <div>Primer asesor: {formatDateTime(item.firstAgentReplyAt)}</div>
                      {item.closedAt && <div>Cerrado: {formatDateTime(item.closedAt)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>Fecha</th>
                  <th className={classes.th}>Cliente</th>
                  <th className={classes.th}>Usuario</th>
                  <th className={classes.th}>Cola</th>
                  <th className={classes.th}>Estrellas</th>
                  <th className={classes.th}>Lectura</th>
                  <th className={classes.th}>Comentario</th>
                </tr>
              </thead>
              <tbody>
                {ratingData.rows.map(item => (
                  <tr key={item.id}>
                    <td className={classes.td}>{formatDateTime(item.createdAt)}</td>
                    <td className={classes.td}><strong>{item.clientName}</strong></td>
                    <td className={classes.td}>{item.agentName}</td>
                    <td className={classes.td}>{item.queueName}</td>
                    <td className={classes.td}>{"★".repeat(item.stars)}{"☆".repeat(Math.max(0, 5 - item.stars))}</td>
                    <td className={classes.td}>
                      <strong>{item.sentiment}</strong>
                      <div style={{ opacity: 0.7 }}>
                        {item.stars <= 2 ? "Conviene revisar la atencion." :
                          item.stars >= 4 ? "Cliente satisfecho." :
                            "Seguimiento sugerido."}
                      </div>
                    </td>
                    <td className={classes.td}>{item.comment || "Sin comentario"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Paper>

      <Dialog
        open={contextModalOpen}
        onClose={handleCloseContextModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {contextRow ? `Contexto del ticket #${contextRow.ticketId}` : "Contexto del ticket"}
        </DialogTitle>
        <DialogContent dividers>
          {contextRow && (
            <Box mb={2}>
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                {contextRow.contactName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {contextRow.userName} · {contextRow.queueName || "Sin cola"} · Estado: {contextRow.status}
              </Typography>
              <Box mt={1} display="flex" flexWrap="wrap" gridGap={8}>
                <TimeMetricChip value={contextRow.firstResponseMinutes} prefix="Primera respuesta" />
                <TimeMetricChip value={contextRow.avgAgentResponseMinutes} prefix="Asesor" />
                <TimeMetricChip value={contextRow.avgClientResponseMinutes} prefix="Cliente" />
                <ReadRateChip value={contextRow.clientReadRatePct} />
              </Box>
            </Box>
          )}

          {contextLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight={220}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper className={classes.card} style={{ marginBottom: 16, boxShadow: "none" }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10 }}>
                  Extracto relacionado con la demora
                </Typography>
                {contextExcerpt.length ? contextExcerpt.map(message => (
                  <Box
                    key={`excerpt-${message.id}`}
                    mb={1.25}
                    p={1.5}
                    borderRadius={12}
                    style={{
                      background: message.fromMe ? "rgba(29,154,108,0.12)" : "rgba(36,69,101,0.08)",
                      border: message.fromMe ? "1px solid rgba(29,154,108,0.18)" : "1px solid rgba(36,69,101,0.12)"
                    }}
                  >
                    <Typography variant="caption" display="block" style={{ fontWeight: 700 }}>
                      {message.fromMe ? "Asesor / sistema" : (message.contact?.name || contextRow?.contactName || "Cliente")} · {formatContextDateTime(message.createdAt)}
                    </Typography>
                    <Typography variant="body2" style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>
                      {getMessagePreviewText(message)}
                    </Typography>
                  </Box>
                )) : (
                  <Typography variant="body2" color="textSecondary">
                    No se encontro un extracto puntual con los mensajes cargados del ticket.
                  </Typography>
                )}
              </Paper>

              <Paper className={classes.card} style={{ boxShadow: "none" }}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 10 }}>
                  Historial cargado del ticket
                </Typography>
                <div style={{ maxHeight: 340, overflowY: "auto", paddingRight: 4 }}>
                  {contextMessages.length ? contextMessages.map(message => (
                    <Box
                      key={`context-${message.id}`}
                      mb={1.25}
                      p={1.5}
                      borderRadius={12}
                      style={{
                        background: message.fromMe ? "rgba(29,154,108,0.08)" : "rgba(36,69,101,0.05)",
                        border: "1px solid rgba(36,69,101,0.10)"
                      }}
                    >
                      <Typography variant="caption" display="block" style={{ fontWeight: 700 }}>
                        {message.fromMe ? "Asesor / sistema" : (message.contact?.name || contextRow?.contactName || "Cliente")} · {formatContextDateTime(message.createdAt)}
                      </Typography>
                      <Typography variant="body2" style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>
                        {getMessagePreviewText(message)}
                      </Typography>
                    </Box>
                  )) : (
                    <Typography variant="body2" color="textSecondary">
                      No se cargaron mensajes para este ticket.
                    </Typography>
                  )}
                </div>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContextModal} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}
